import { useEffect, useRef } from "react";
import { wsBase } from "./api.js";

/**
 * The live connection to a match, with polling underneath it.
 *
 * The socket is an optimisation, not a dependency. A scorer on weak signal at a
 * ground needs the score to keep moving without it, so polling starts the
 * moment the socket is not open and stops the moment it is.
 *
 * The callbacks are held in a ref and updated in an effect, NOT during render.
 * Assigning during render is the obvious way to write this and it is wrong:
 * React may render twice before committing, so a handler that counts — and
 * accept() counts optimistic updates — sees the same value applied twice. The
 * first attempt at this hook did exactly that, and every ball a scorer entered
 * was then judged stale and thrown away. Unit tests were all green; only
 * scoring a match offline caught it.
 */
export interface MatchSocketOptions {
  matchId: string;
  lease: string | null;
  load: () => void;
  onSnapshot: (snap: unknown) => void;
  onSignal: (msg: Record<string, unknown>) => void;
  onLeaseLost: () => void;
  onConnection: (state: "live" | "polling" | "offline") => void;
}

export function useMatchSocket(o: MatchSocketOptions) {
  const ws = useRef<WebSocket | null>(null);
  const cb = useRef(o);
  // After commit, never during render.
  useEffect(() => { cb.current = o; });

  useEffect(() => {
    let alive = true;
    let poll: ReturnType<typeof setInterval> | null = null;
    let retry = 2000;

    const startPolling = () => {
      if (poll) return;
      // 2s, not 5. At 5s a scorer watching a spectator screen sees the ball
      // land late enough to look broken.
      poll = setInterval(() => { if (navigator.onLine) cb.current.load(); }, 2000);
    };
    const stopPolling = () => { if (poll) { clearInterval(poll); poll = null; } };

    const open = () => {
      if (!alive) return;
      const role = o.lease ? "scorer" : "spectator";
      const url = `${wsBase()}/api/v1/matches/${o.matchId}/live?role=${role}` +
                  (o.lease ? `&lease=${encodeURIComponent(o.lease)}` : "");
      let sock: WebSocket;
      try { sock = new WebSocket(url); }
      catch { cb.current.onConnection("offline"); startPolling(); return; }

      ws.current = sock;
      sock.onopen = () => { retry = 2000; stopPolling(); cb.current.onConnection("live"); };
      sock.onclose = () => {
        if (!alive) return;
        cb.current.onConnection("polling");
        startPolling();
        retry = Math.min(retry * 1.8, 30000);
        setTimeout(open, retry);
      };
      sock.onmessage = (ev) => {
        try {
          const m = JSON.parse(String(ev.data)) as { type?: string; snapshot?: unknown };
          if (typeof m.type === "string" && m.type.startsWith("RTC_")) {
            cb.current.onSignal(m as unknown as Record<string, unknown>);
            return;
          }
          if (m.snapshot) cb.current.onSnapshot(m.snapshot);
          if (m.type === "LEASE_TAKEN_OVER" || m.type === "LEASE_EXPIRED") cb.current.onLeaseLost();
        } catch { /* ignore malformed frames */ }
      };
    };

    open();
    return () => {
      alive = false;
      stopPolling();
      try { ws.current?.close(); } catch { /* already closing */ }
    };
  }, [o.matchId, o.lease]);

  return ws;
}
